import { MaxChatMessage, MaxConversationSummary } from '../types'
import { MAX_RUNTIME_CONFIG } from '../config/max-env.config'
import { ChartConfig, ChatAttachment, FlowMetadata } from '../core/types/chat.types'
import { normalizeFlowMetadata } from '../utils/flow'
import { maxDebug, maxMessageDebug } from '../utils/max-debug'

type BackendConversation = {
  conversationId?: string
  title?: string
  preview?: string
  updatedAt?: string
  createdAt?: string
}

type BackendConversationsResponse = {
  conversations?: BackendConversation[]
  pinnedConversations?: BackendConversation[]
}

type BackendMessage = {
  messageId?: string
  id?: string
  role?: string
  type?: string
  content?: string
  body?: string
  timestamp?: string
  createdAt?: string
  conversationId?: string
  attachments?: BackendAttachment[] | ChatAttachment[] | string[]
  fav?: boolean
  responseType?: string
  is_flow?: boolean
  flow_metadata?: FlowMetadata
  graphs?: ChartConfig[]
}

type BackendAttachment = {
  uniqueFilename: string
  userFilename: string
  contentType?: string
  size?: number
  kind?: ChatAttachment['kind']
}

const assertApiBaseUrl = () => {
  if (!MAX_RUNTIME_CONFIG.api.baseUrl) {
    throw new Error('MAX API base URL is not configured (REACT_APP_MAX_API).')
  }
}

const getAuthHeaders = (chatToken: string): HeadersInit => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${chatToken}`,
})

const mapBackendConversation = (raw: BackendConversation): MaxConversationSummary | null => {
  if (!raw.conversationId) return null

  return {
    id: raw.conversationId,
    title: raw.title || 'Untitled Conversation',
    preview: raw.preview || '',
    updatedAt: raw.updatedAt
      ? new Date(raw.updatedAt).getTime()
      : raw.createdAt
      ? new Date(raw.createdAt).getTime()
      : Date.now(),
  }
}

const normalizeRole = (raw: BackendMessage): 'user' | 'assistant' => {
  const type = (raw.type || '').toLowerCase()
  const role = (raw.role || '').toLowerCase()
  if (type === 'user' || role === 'user') return 'user'
  if (type === 'system' || role === 'assistant') return 'assistant'
  if (type === 'ai') return 'assistant'
  return role === 'system' ? 'assistant' : 'user'
}

const mapBackendMessage = (
  raw: BackendMessage,
  fallbackConversationId: string
): MaxChatMessage | null => {
  const messageId = raw.messageId || raw.id
  if (!messageId) return null

  const attachments =
    Array.isArray(raw.attachments) && raw.attachments.length > 0
      ? raw.attachments.map((attachment) => {
          if (typeof attachment === 'string') {
            return {
              uniqueFilename: attachment,
              userFilename: attachment,
              contentType: 'application/octet-stream',
              size: 0,
            }
          }

          return {
            uniqueFilename: attachment.uniqueFilename,
            userFilename: attachment.userFilename,
            contentType: attachment.contentType || 'application/octet-stream',
            size: attachment.size ?? 0,
            kind: attachment.kind,
          }
        })
      : undefined

  return {
    id: messageId,
    messageId,
    conversationId: raw.conversationId || fallbackConversationId,
    role: normalizeRole(raw),
    content: raw.content || raw.body || '',
    createdAt: raw.timestamp
      ? new Date(raw.timestamp).getTime()
      : raw.createdAt
      ? new Date(raw.createdAt).getTime()
      : Date.now(),
    attachments,
    fav: raw.fav,
    responseType: raw.responseType || raw.type,
    isFlow: raw.is_flow,
    flowMetadata: normalizeFlowMetadata(raw.flow_metadata) || undefined,
    graphs: raw.graphs,
  }
}

export const fetchMaxConversations = async (
  chatToken: string,
  tenantId: string
): Promise<MaxConversationSummary[]> => {
  assertApiBaseUrl()

  const url = new URL(`${MAX_RUNTIME_CONFIG.api.baseUrl}/conversations`)
  url.searchParams.set('tenantId', tenantId)

  let response: Response
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders(chatToken),
    })
  } catch (error) {
    const typedError = error instanceof Error ? error : new Error(String(error))
    maxDebug.error('GET /conversations - fetch failed before response', {
      url: url.toString(),
      name: typedError.name,
      message: typedError.message,
    })
    throw typedError
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    maxDebug.error('GET /conversations - non-ok response', {
      status: response.status,
      body: errorBody || 'No response body',
    })
    throw new Error(
      `MAX /conversations failed (${response.status}). ${errorBody || 'No response body'}`
    )
  }

  const data = (await response.json()) as BackendConversationsResponse
  const conversations = data.conversations || []
  const pinnedConversations = data.pinnedConversations || []

  const merged = [...pinnedConversations, ...conversations]
    .map(mapBackendConversation)
    .filter((item): item is MaxConversationSummary => item !== null)

  const uniqueById = new Map<string, MaxConversationSummary>()
  merged.forEach((conversation) => {
    uniqueById.set(conversation.id, conversation)
  })

  return Array.from(uniqueById.values()).sort((a, b) => b.updatedAt - a.updatedAt)
}

export const fetchMaxMessages = async (
  chatToken: string,
  tenantId: string,
  conversationId: string
): Promise<MaxChatMessage[]> => {
  assertApiBaseUrl()

  const url = new URL(`${MAX_RUNTIME_CONFIG.api.baseUrl}/messages`)
  url.searchParams.set('tenantId', tenantId)
  url.searchParams.set('conversationId', conversationId)

  let response: Response
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders(chatToken),
    })
  } catch (error) {
    const typedError = error instanceof Error ? error : new Error(String(error))
    maxDebug.error('GET /messages - fetch failed before response', {
      url: url.toString(),
      name: typedError.name,
      message: typedError.message,
    })
    throw typedError
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    maxDebug.error('GET /messages - non-ok response', {
      status: response.status,
      body: errorBody || 'No response body',
    })
    throw new Error(`MAX /messages failed (${response.status}). ${errorBody || 'No response body'}`)
  }

  const data = (await response.json()) as BackendMessage[] | { messages?: BackendMessage[] }
  const rawMessages = Array.isArray(data) ? data : data.messages || []
  const mappedMessages = rawMessages
    .map((message) => mapBackendMessage(message, conversationId))
    .filter((message): message is MaxChatMessage => message !== null)

  maxMessageDebug.state('History fetch mapped messages', {
    conversationId,
    tenantId,
    rawCount: rawMessages.length,
    mappedCount: mappedMessages.length,
    messages: mappedMessages,
  })

  return mappedMessages
}
